import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from './firebase';

type UnknownRecord = Record<string, unknown>;

export type AdminMessageType = 'text' | 'image';
export type AdminMessageUserRole = 'donor' | 'donee' | 'unknown';

export interface AdminMessageUserSummary {
  id: string;
  displayName: string;
  email: string;
  role: AdminMessageUserRole;
  status?: string;
  iconUrl?: string;
}

export interface AdminConversationSummary {
  id: string;
  participants: string[];
  participantSummaries: AdminMessageUserSummary[];
  lastMessage?: string;
  lastMessageAt?: Date;
  lastMessageBy?: string;
  createdAt?: Date;
  updatedAt?: Date;
  unreadTotal: number;
}

export interface AdminMessage {
  id: string;
  senderId: string;
  senderName: string;
  content: string;
  type: AdminMessageType;
  imageUrl?: string;
  sentAt?: Date;
  readAt?: Date;
}

export interface AdminConversationDetail {
  conversation: AdminConversationSummary;
  messages: AdminMessage[];
}

const asOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const asDate = (value: unknown): Date | undefined => {
  if (value && typeof value === 'object' && 'toDate' in value) {
    const toDate = (value as { toDate?: unknown }).toDate;
    if (typeof toDate === 'function') {
      return toDate.call(value) as Date;
    }
  }
  return undefined;
};

const asUserRole = (value: unknown): AdminMessageUserRole => {
  if (value === 'donor' || value === 'donee') {
    return value;
  }
  return 'unknown';
};

const parseParticipants = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
};

const parseUnreadTotal = (value: unknown): number => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return 0;
  }

  return Object.values(value as Record<string, unknown>).reduce<number>((total, count) => {
    if (typeof count !== 'number' || Number.isNaN(count)) {
      return total;
    }
    return total + Math.max(0, Math.floor(count));
  }, 0);
};

async function fetchUserSummary(userId: string): Promise<AdminMessageUserSummary> {
  if (!userId) {
    return {
      id: '',
      displayName: '不明なユーザー',
      email: '',
      role: 'unknown',
    };
  }

  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) {
    return {
      id: userId,
      displayName: '不明なユーザー',
      email: '',
      role: 'unknown',
    };
  }

  const data = userSnap.data() as UnknownRecord;
  return {
    id: userId,
    displayName: asOptionalString(data.displayName) ?? '名前未設定',
    email: asOptionalString(data.email) ?? '',
    role: asUserRole(data.role),
    status: asOptionalString(data.status),
    iconUrl: asOptionalString(data.iconUrl),
  };
}

async function mapConversation(
  conversationId: string,
  data: UnknownRecord,
  userSummaries: Map<string, AdminMessageUserSummary>
): Promise<AdminConversationSummary> {
  const participants = parseParticipants(data.participants);
  const missingUserIds = participants.filter((userId) => !userSummaries.has(userId));

  await Promise.all(
    missingUserIds.map(async (userId) => {
      userSummaries.set(userId, await fetchUserSummary(userId));
    })
  );

  return {
    id: conversationId,
    participants,
    participantSummaries: participants.map((userId) => userSummaries.get(userId)).filter(Boolean) as AdminMessageUserSummary[],
    lastMessage: asOptionalString(data.lastMessage),
    lastMessageAt: asDate(data.lastMessageAt),
    lastMessageBy: asOptionalString(data.lastMessageBy),
    createdAt: asDate(data.createdAt),
    updatedAt: asDate(data.updatedAt),
    unreadTotal: parseUnreadTotal(data.unreadBy),
  };
}

export async function fetchAdminConversations(
  maxCount: number = 200
): Promise<AdminConversationSummary[]> {
  const conversationsQuery = query(
    collection(db, 'conversations'),
    orderBy('updatedAt', 'desc'),
    limit(maxCount)
  );
  const snapshot = await getDocs(conversationsQuery);
  const userSummaries = new Map<string, AdminMessageUserSummary>();

  const conversations = await Promise.all(
    snapshot.docs.map((conversationDoc) =>
      mapConversation(
        conversationDoc.id,
        conversationDoc.data() as UnknownRecord,
        userSummaries
      )
    )
  );

  return conversations.sort(
    (a, b) =>
      (b.updatedAt?.getTime() ?? b.lastMessageAt?.getTime() ?? 0) -
      (a.updatedAt?.getTime() ?? a.lastMessageAt?.getTime() ?? 0)
  );
}

export async function fetchAdminConversationDetail(
  conversationId: string,
  maxMessages: number = 200
): Promise<AdminConversationDetail | null> {
  const conversationSnap = await getDoc(doc(db, 'conversations', conversationId));
  if (!conversationSnap.exists()) {
    return null;
  }

  const userSummaries = new Map<string, AdminMessageUserSummary>();
  const conversation = await mapConversation(
    conversationSnap.id,
    conversationSnap.data() as UnknownRecord,
    userSummaries
  );
  const messagesQuery = query(
    collection(db, 'conversations', conversationId, 'messages'),
    orderBy('sentAt', 'asc'),
    limit(maxMessages)
  );
  const messagesSnap = await getDocs(messagesQuery);

  const messages = messagesSnap.docs.map((messageDoc) => {
    const data = messageDoc.data() as UnknownRecord;
    const senderId = asOptionalString(data.senderId) ?? '';

    return {
      id: messageDoc.id,
      senderId,
      senderName: userSummaries.get(senderId)?.displayName ?? '不明なユーザー',
      content: asOptionalString(data.content) ?? '',
      type: data.type === 'image' ? 'image' : 'text',
      imageUrl: asOptionalString(data.imageUrl),
      sentAt: asDate(data.sentAt),
      readAt: asDate(data.readAt),
    } satisfies AdminMessage;
  });

  return {
    conversation,
    messages,
  };
}
